import React, { useState, useEffect } from 'react';
import { X, Clock, Film, Tv, Flame, PieChart, Sparkles, Award, Calendar } from 'lucide-react';
import { api, type WatchStats } from '../lib/api';
import { useThemeLanguage } from '../context/ThemeLanguageContext';

export interface PersonalStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  profileName?: string;
}

export function PersonalStatsModal({ isOpen, onClose, profileName }: PersonalStatsModalProps) {
  const { t, language } = useThemeLanguage();
  const [stats, setStats] = useState<WatchStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      api
        .getPersonalStats()
        .then((data) => setStats(data))
        .catch((err) => console.error('Failed to load stats', err))
        .finally(() => setLoading(false));
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-xl flex items-center justify-center p-4 animate-in fade-in" dir="rtl">
      <div className="bg-neutral-950 border border-white/20 rounded-3xl p-6 sm:p-8 max-w-2xl w-full shadow-2xl relative overflow-hidden">
        {/* Ambient Top Glow */}
        <div className="absolute top-0 right-1/4 w-72 h-32 bg-red-600/15 rounded-full blur-[70px] pointer-events-none" />

        {/* Header */}
        <div className="flex items-center justify-between pb-4 mb-6 border-b border-white/10 relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-red-600 to-amber-600 flex items-center justify-center text-white shadow-lg shadow-red-950/50">
              <Award className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-black text-white flex items-center gap-2">
                <span>{language === 'he' ? 'דשבורד צפייה אישי' : 'My Cinema Stats'}</span>
                {profileName && (
                  <span className="text-xs px-2.5 py-0.5 rounded-full bg-white/10 text-neutral-300 font-medium">
                    {profileName}
                  </span>
                )}
              </h2>
              <p className="text-xs text-neutral-400 mt-0.5">
                {language === 'he' ? 'סיכום הרגלי הצפייה וההישגים שלך ב-Homeflix' : 'Your personal watch habits & records in Homeflix'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 text-neutral-400 hover:text-white transition-colors cursor-pointer"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {loading ? (
          <div className="py-20 flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#E50914]" />
          </div>
        ) : !stats ? (
          <div className="text-center py-16 text-neutral-400">
            <p>{language === 'he' ? 'אין עדיין נתוני צפייה זמינים' : 'No watch statistics recorded yet'}</p>
          </div>
        ) : (
          <div className="space-y-6 relative z-10">
            {/* Top 4 Key Metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {/* Total Hours */}
              <div className="p-4 rounded-2xl bg-neutral-900/90 border border-white/10 text-center relative overflow-hidden group hover:border-red-500/40 transition-all">
                <Clock className="w-5 h-5 text-red-500 mx-auto mb-1.5" />
                <div className="text-2xl sm:text-3xl font-black text-white">
                  {stats.totalHoursWatched}
                </div>
                <div className="text-[11px] font-bold text-neutral-400 uppercase tracking-wider mt-0.5">
                  {language === 'he' ? 'שעות צפייה' : 'Hours Watched'}
                </div>
              </div>

              {/* Movies Finished */}
              <div className="p-4 rounded-2xl bg-neutral-900/90 border border-white/10 text-center relative overflow-hidden group hover:border-blue-500/40 transition-all">
                <Film className="w-5 h-5 text-blue-400 mx-auto mb-1.5" />
                <div className="text-2xl sm:text-3xl font-black text-white">
                  {stats.completedMovies}
                </div>
                <div className="text-[11px] font-bold text-neutral-400 uppercase tracking-wider mt-0.5">
                  {language === 'he' ? 'סרטים שהושלמו' : 'Movies Finished'}
                </div>
              </div>

              {/* Episodes Finished */}
              <div className="p-4 rounded-2xl bg-neutral-900/90 border border-white/10 text-center relative overflow-hidden group hover:border-purple-500/40 transition-all">
                <Tv className="w-5 h-5 text-purple-400 mx-auto mb-1.5" />
                <div className="text-2xl sm:text-3xl font-black text-white">
                  {stats.completedEpisodes}
                </div>
                <div className="text-[11px] font-bold text-neutral-400 uppercase tracking-wider mt-0.5">
                  {language === 'he' ? 'פרקים שנצפו' : 'Episodes Finished'}
                </div>
              </div>

              {/* Streak Days */}
              <div className="p-4 rounded-2xl bg-neutral-900/90 border border-white/10 text-center relative overflow-hidden group hover:border-amber-500/40 transition-all">
                <Flame className="w-5 h-5 text-amber-500 mx-auto mb-1.5 animate-pulse" />
                <div className="text-2xl sm:text-3xl font-black text-white">
                  {stats.watchStreakDays}
                </div>
                <div className="text-[11px] font-bold text-neutral-400 uppercase tracking-wider mt-0.5">
                  {language === 'he' ? 'ימי רצף (Streak)' : 'Day Streak'}
                </div>
              </div>
            </div>

            {/* Top Genres Breakdown */}
            <div className="p-5 rounded-2xl bg-neutral-900/80 border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                  <PieChart className="w-4 h-4 text-emerald-400" />
                  <span>{language === 'he' ? 'הז׳אנרים האהובים עליך' : 'Your Top Genres'}</span>
                </h3>
                {stats.favoriteYear && (
                  <span className="text-xs text-neutral-400 flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>{language === 'he' ? `שנת שיא: ${stats.favoriteYear}` : `Top Era: ${stats.favoriteYear}`}</span>
                  </span>
                )}
              </div>

              {stats.topGenres.length === 0 ? (
                <p className="text-xs text-neutral-500">
                  {language === 'he' ? 'צפה ביותר תכנים כדי לגלות את הז׳אנרים המובילים שלך' : 'Watch more media to uncover your favorite genres'}
                </p>
              ) : (
                <div className="space-y-3">
                  {stats.topGenres.map((g, i) => (
                    <div key={g.genre} className="space-y-1">
                      <div className="flex justify-between text-xs font-semibold">
                        <span className="text-neutral-200">{g.genre}</span>
                        <span className="text-neutral-400 font-mono">{g.percentage}% ({g.count})</span>
                      </div>
                      <div className="w-full h-2 rounded-full bg-neutral-800 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-red-600 to-amber-500 transition-all duration-500"
                          style={{ width: `${Math.max(5, g.percentage)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Fun Cinephile Badge */}
            <div className="p-4 rounded-2xl bg-gradient-to-r from-red-950/50 via-neutral-900 to-neutral-900 border border-red-500/20 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sparkles className="w-6 h-6 text-amber-400" />
                <div>
                  <h4 className="text-sm font-bold text-white">
                    {stats.totalHoursWatched > 50
                      ? language === 'he' ? 'קולנוען מקצועי 🏆' : 'Cinema Connoisseur 🏆'
                      : stats.totalHoursWatched > 10
                      ? language === 'he' ? 'חובב קולנוע מושבע 🍿' : 'Avid Moviegoer 🍿'
                      : language === 'he' ? 'צופה מתחיל 🎬' : 'Cinephile in Training 🎬'}
                  </h4>
                  <p className="text-xs text-neutral-400">
                    {language === 'he' ? `סך כולל של ${stats.totalPlays} כניסות לניגון מדיה` : `${stats.totalPlays} total play sessions logged`}
                  </p>
                </div>
              </div>

              <button
                onClick={onClose}
                className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-xs transition-colors cursor-pointer"
              >
                {language === 'he' ? 'סגור' : 'Close'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
